/**
 * Server-side cleanup trigger for stale RTDB alert entries.
 *
 * Fires the `cleanup_realtime_alerts` sub-action of the proxy CF
 * (`new_callServiceAuthorizedAPI`) which runs an admin-SDK
 * `orderByChild('t').endAt(cutoff)` query server-side and bulk-deletes
 * anything older than 2 minutes under
 * `/instantUserConversationMessageAlerts/{ext|extforpage}/{uid}/`.
 *
 * Server-side execution keeps the cleanup off the wire from the
 * client's perspective — exactly the bandwidth that the bloat was
 * costing us in the first place.
 *
 * Throttling: fires at most once per page-load. After the first
 * successful (or attempted) trigger, subsequent calls are no-ops until
 * the page reloads. The cleanup window is generous (2 min) so a single
 * sweep at session start covers the entire session — new entries from
 * CF aren't old enough to qualify until they're abandoned, which the
 * unbounded-onChildChanged + limitToLast(1)-onChildAdded mix on the
 * listener handles anyway.
 *
 * If the call fails (network blip, auth not ready), the throttle is
 * cleared so the next attach can retry.
 */

import { callServiceAPI } from '@/lib/firebase/functions';
import { useAuthStore } from '@/stores/auth-store';

let cleanupTriggered = false;

export function triggerAlertsCleanup(extensionName: string): void {
  if (cleanupTriggered) return;
  if (!extensionName) return;

  const auth = useAuthStore.getState();
  const adminUid = auth.selectedAdminUserId || undefined;
  const ownUid = auth.user?.uid;
  const targetUid = adminUid || ownUid;
  if (!targetUid) {
    // Auth not ready yet — bail without latching, so the next attach
    // (after auth resolves) gets a chance to fire.
    return;
  }

  cleanupTriggered = true;
  console.log('[rtdb-cleanup] firing CF call', { extensionName, targetUid, adminUid });

  callServiceAPI({
    appCode: extensionName,
    url: 'cleanup_realtime_alerts',
    method: 'post',
    // CF resolves targetUid = _ua_lic_adminUserID || caller.uid — same
    // shared-user precedence the rest of the codebase uses for inbox
    // paths (see sms-functions.js inboxOwnerID resolution at L630+).
    _ua_lic_adminUserID: adminUid,
  })
    .then((result) => {
      console.log('[rtdb-cleanup] CF returned', result?.data);
    })
    .catch((err) => {
      console.warn('[rtdb-cleanup] CF call failed', err);
      cleanupTriggered = false;  // allow retry on next attach
    });
}
